/**
 * Organization Storage Utility
 *
 * Provides functions to persist and retrieve the currently selected organization.
 * Used for organization switching functionality in the dashboard.
 *
 * This mirrors the old project's user_and_organization.storage.ts functionality
 * but uses the new project's storageClient pattern.
 */

import { storageClient } from "../clients";

// ============================================================================
// TYPES
// ============================================================================

/**
 * Organization type (farmer or broker)
 */
export type OrgType = "farmer" | "broker";

/**
 * Selected organization data structure
 */
export interface SelectedOrganization {
  orgId: number;
  orgName: string;
  orgType: OrgType;
}

// ============================================================================
// STORAGE KEYS
// ============================================================================

/**
 * LocalStorage keys for organization data
 * Using consistent naming with the old project for compatibility
 */
const STORAGE_KEYS = {
  ORG_ID: "organizationId",
  ORG_NAME: "organizationName",
  ORG_TYPE: "orgType",
} as const;

// ============================================================================
// FUNCTIONS
// ============================================================================

/**
 * Store the selected organization in localStorage
 *
 * @param orgId - Organization ID
 * @param orgName - Organization name
 * @param orgType - Organization type (farmer or broker)
 */
export const setSelectedOrganization = (
  orgId: number,
  orgName: string,
  orgType: OrgType
): void => {
  storageClient.setLocalStorage(STORAGE_KEYS.ORG_ID, orgId.toString());
  storageClient.setLocalStorage(STORAGE_KEYS.ORG_NAME, orgName);
  storageClient.setLocalStorage(STORAGE_KEYS.ORG_TYPE, orgType);
};

/**
 * Get the selected organization from localStorage
 *
 * @returns Selected organization data or null if not set
 */
export const getSelectedOrganization = (): SelectedOrganization | null => {
  // Pass explicit fallback so behavior is correct when key is missing or during SSR
  const orgIdStr = storageClient.getLocalStorage<string | null>(
    STORAGE_KEYS.ORG_ID,
    null
  );
  const orgName = storageClient.getLocalStorage<string | null>(
    STORAGE_KEYS.ORG_NAME,
    null
  );
  const orgTypeStr = storageClient.getLocalStorage<string | null>(
    STORAGE_KEYS.ORG_TYPE,
    null
  );

  // Return null if any required field is missing
  if (!orgIdStr || !orgName || !orgTypeStr) {
    return null;
  }

  const orgId = parseInt(orgIdStr, 10);
  if (isNaN(orgId)) {
    return null;
  }

  // Validate orgType
  const orgType = orgTypeStr === "broker" ? "broker" : "farmer";

  return {
    orgId,
    orgName,
    orgType,
  };
};

/**
 * Get only the organization ID from localStorage
 *
 * @returns Organization ID or null if not set
 */
export const getSelectedOrganizationId = (): number | null => {
  const orgIdStr = storageClient.getLocalStorage<string | null>(
    STORAGE_KEYS.ORG_ID,
    null
  );
  if (!orgIdStr) {
    return null;
  }

  const orgId = parseInt(orgIdStr, 10);
  return isNaN(orgId) ? null : orgId;
};

/**
 * Get only the organization type from localStorage
 *
 * @returns Organization type or null if not set
 */
export const getSelectedOrganizationType = (): OrgType | null => {
  const orgTypeStr = storageClient.getLocalStorage<string | null>(
    STORAGE_KEYS.ORG_TYPE,
    null
  );
  if (!orgTypeStr) {
    return null;
  }

  return orgTypeStr === "broker" ? "broker" : "farmer";
};

/**
 * Clear the selected organization from localStorage
 */
export const clearSelectedOrganization = (): void => {
  storageClient.removeLocalStorage(STORAGE_KEYS.ORG_ID);
  storageClient.removeLocalStorage(STORAGE_KEYS.ORG_NAME);
  storageClient.removeLocalStorage(STORAGE_KEYS.ORG_TYPE);
};

/**
 * Check if an organization is currently selected
 *
 * @returns True if an organization is selected in storage
 */
export const hasSelectedOrganization = (): boolean => {
  const value = storageClient.getLocalStorage<string | null>(
    STORAGE_KEYS.ORG_ID,
    null
  );
  return value !== null && value !== undefined && value !== "";
};
